import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import RemoveDefaultButtonStyles from '../styles/RemoveDefaultButtonStyles';
import IconMapLazy from '../definitions/IconMapLazy';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import MessagePropType from '../definitions/MessagePropType';
import messageToString from '../functions/messageToString';
import IconChooser from './IconChooser';


function ButtonGroup({
    options,
    initialSelectedIndex = 0,
    onChange,
    disabled = false,
    width,
    className,
    ...otherProps
}) {
    if (!options || !options.length) {
        throw new Error('[ButtonGroup]: options prop must be an array of ButtonGroupOptionPropType and cannot be empty.');
    }

    const intl = useIntl();

    const [ activeIndex, setActiveIndex ] = useState(initialSelectedIndex);
    const [ itemMinWidth, setItemMinWidth ] = useState(null);

    // Save a ref to the Container element so we can measure it.
    const containerRef = useRef(null);

    const allTitles = options.map(option => option.title);

    // GOAL: Use ResizeObserver to handle triggering reflow() when button
    // content is resized. This happens when font size or weight changes.
    useEffect(() => {
        let observer;

        // NOTE: containerRef.current will always be available in any effects
        // or event handlers because the element it belongs to is this component and
        // this component is never unmounted or rendered conditionally. The ref is
        // also never re-assigned to another element so we don't need to include
        // in this effect's dependency array.
        const containerElement = containerRef?.current;

        // Just check for the ref first just to be sure it exists.
        if (containerElement) {
            observer = new ResizeObserver(() => {
                reflow();
            });
            const contentWrappers = containerElement.querySelectorAll('.content-wrapper');
            contentWrappers.forEach(wrapper => {
                observer?.observe(wrapper);
            });
        }

        return () => {
            observer?.disconnect();
        };
    }, [ ]);

    // Handle triggering reflow() when titles change in options prop.
    useEffect(() => {
        reflow();
    // Our linter is way too picky about dependencies, this is fine
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ ...allTitles ]);

    // Determine width of widest list item's content, set that to be
    // the min-width for all list item elements.
    function reflow() {
        const containerElement = containerRef?.current;

        if (containerElement) {
            let widestElementLength = 0;
            const contentWrappers = containerElement.querySelectorAll('.content-wrapper');
            if (contentWrappers) {
                contentWrappers.forEach(wrapperElement => {
                    if (wrapperElement.clientWidth > widestElementLength) {
                        widestElementLength = wrapperElement.clientWidth;
                    }
                });
                setItemMinWidth(widestElementLength);
            }
        }
    }

    function handleListItemOnClick(index) {
        setActiveIndex(index);
        onChange?.(index);
    }

    const computedOptions = options.map(option => ({
        ...option,

        // ensure disabled is always set, whether it's true or false.
        disabled: !!(disabled || option.disabled),
    }));

    // Allow focusing previous/next buttons using arrow keys. Since these are
    // buttons, Enter or Space will activate the focused option. Notably, focus
    // will not cycle around to the start/end when you try to arrow past the
    // first/last one. We could add that if we want.
    function handleKeyDown(e) {
        const listItem = e.target.closest('.list-item');

        let nextSibling;
        let prevSibling;
        let nextButton;
        let prevButton;

        switch (e.key) {
            case 'ArrowRight':
                nextSibling = getNextEnabledSibling(listItem);
                if (nextSibling) {
                    nextButton = nextSibling.querySelector('[data-name]');
                    if (nextButton) {
                        nextButton.focus();
                    }
                }
                break;
            case 'ArrowLeft':
                prevSibling = getPrevEnabledSibling(listItem);
                if (prevSibling) {
                    prevButton = prevSibling.querySelector('[data-name]');
                    if (prevButton) {
                        prevButton.focus();
                    }
                }
                break;
        }
    }

    return (
        <Container
            className={classNames(
                'button-group-container rh-display-flex',
                'rh-justify-content-center rh-align-items-center',
                'rh-list-style-type-none rh-m-0 rh-p-0',
                className,
            )}
            onKeyDown={handleKeyDown}
            ref={containerRef}

            /* REQUIREMENT: When width is not passed, ButtonGroup is expected to
            take up the full width of parent container */
            $width={width}

            /* Setting min-width on list items ensures that buttons are all
            one uniform size (the width of the largest content OR larger if
            width is in use and more space is available). */
            $itemMinWidth={itemMinWidth}

            {...otherProps}
        >
            <For
                each="button"
                of={computedOptions}
                index="index"
            >
                <li
                    key={`button-${button.id}`}
                    className={classNames(
                        'list-item rh-flex-grow-1',
                        {
                            'is-active': index === activeIndex,
                            'is-disabled': button.disabled,
                        },
                    )}
                >
                    <button
                        className={classNames(
                            'button rh-display-flex rh-justify-content-center',
                            'rh-align-items-center rh-width-100p',
                            'rh-border-color-blueberry-dark',
                            'rh-border-style-solid',
                        )}
                        onClick={() => handleListItemOnClick(index)}
                        type="button"
                        disabled={button.disabled}
                        title={button.accessibilityTitle}
                        data-name={`button-group-button-${button.id}`}
                    >
                        {/* We measure all .content-wrapper div widths on container resize.
                            Each one represents the total width of the content in each button. */}
                        <div
                            className={
                                classNames(
                                    'content-wrapper rh-display-flex',
                                    'rh-justify-content-center',
                                    'rh-align-items-center rh-gap-0_5',
                                    'rh-no-wrap',
                                    {
                                        // Only needed when icon is present to account for whitespace in icon
                                        'rh-pr-0_75': button.iconKey,
                                    },
                                )
                            }
                        >
                            <If condition={button.iconKey}>
                                <IconChooser
                                    className="rh-flex-shrink-0"
                                    size="M"
                                    iconKey={button.iconKey}
                                    stroke={button.disabled
                                        ? Colours.GREY_700
                                        : Colours.BLUEBERRY_DARK}
                                    includeOutline={false}
                                />
                            </If>
                            <If condition={button.title}>
                                <span
                                    className={classNames(
                                        'label rh-font-size-m',
                                        {
                                            'weight-medium': index === activeIndex,
                                            'weight-regular': index !== activeIndex,
                                        }
                                    )}
                                >
                                    {messageToString(button.title, intl)}
                                </span>
                            </If>
                        </div>
                    </button>
                </li>
            </For>
        </Container>
    );
}

const ButtonGroupOptionPropType = PropTypes.shape({
    id: PropTypes.string.isRequired,
    // Neither title or accessibilityTitle are required, but please pass at least one.
    title: MessagePropType,
    accessibilityTitle: MessagePropType,
    iconKey: PropTypes.oneOf(Object.keys(IconMapLazy)),
    disabled: PropTypes.bool,
});

ButtonGroup.propTypes = {
    options: PropTypes.arrayOf(ButtonGroupOptionPropType).isRequired,
    initialSelectedIndex: PropTypes.number,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    width: PropTypes.string, // a CSS length as a string (includes unit e.g. `20rem` or `50%`).
    className: PropTypes.string,
};

const Container = styled.ul`
    --border-radius: 6px;

    ${props => props.$width && `
        width: ${props.$width};
    `}

    ${props => props.$itemMinWidth !== null && `
        min-width: ${props.$itemMinWidth}px;
    `}

    > .list-item {
        flex-shrink: 0;
        flex-basis: 0;

        &:not(:first-child, :last-child) {
            > .button {
                border-width: 1px 0;
            }
        }
        &:first-child {
            > .button {
                border-width: 1px 0 1px 1px;
                border-top-left-radius: var(--border-radius);
                border-bottom-left-radius: var(--border-radius);
            }
        }
        &:last-child {
            > .button {
                border-width: 1px 1px 1px 0;
                border-top-right-radius: var(--border-radius);
                border-bottom-right-radius: var(--border-radius);
            }
        }

        &.is-active {
            > .button {
                background-color: ${Colours.BLUEBERRY_LIGHTEST};
            }
        }

        &.is-disabled {
            > .button {
                background-color: ${Colours.GREY_100};
                border-color: ${Colours.GREY_500};

                > .label {
                    color: ${Colours.GREY_700};
                }
            }
        }

        &.is-active.is-disabled {
            > .button {
                background-color: ${Colours.GREY_200};
            }
        }

        > .button {
            ${RemoveDefaultButtonStyles};
            padding: auto ${Sizes.SPACING.TWO};
            background-color: ${Colours.COCONUT};

            /* IMPORTANT: We want to use an explicit height here, not vertical padding.
            If we use vertical padding, then the amount of padding on buttons with/without
            icons will be different resulting in buttons with borders that don't line up. */
            height: ${Sizes.SPACING.THREE};

            > .label {
                color: ${Colours.BLUEBERRY_DARK};
            }

            &:focus {
                outline: 2px solid ${Colours.BLUEBERRY_DARK};
                position: relative;
                border-width: 0;    /* prevent doubling-up of border-width and outline */
            }
        }
    }
`;

// Find the next sibling element which is *not* disabled
function getNextEnabledSibling(element) {
    const nextSibling = element.nextElementSibling;
    if (!nextSibling) {
        return false; // user is at the end
    } else {
        const childButton = nextSibling.querySelector('.button');
        if (childButton && childButton.disabled) {
            return getNextEnabledSibling(nextSibling); // try the next one
        }

        return nextSibling; // this sibling's button is enabled, return that
    }
}

// Find the previous sibling element which is *not* disabled
function getPrevEnabledSibling(element) {
    const prevSibling = element.previousElementSibling;
    if (!prevSibling) {
        return false; // user is at the start
    } else {
        const childButton = prevSibling.querySelector('.button');
        if (childButton && childButton.disabled) {
            return getPrevEnabledSibling(prevSibling); // try the previous one
        }

        return prevSibling; // this sibling's button is enabled, return that
    }
}

export default ButtonGroup;
export { ButtonGroupOptionPropType };
